import api from './authService';

export interface SendMessageRequest {
    subject: string;
    body: string;
    recipientUserIds?: string[];
    eventId?: string;
    sendToAllUsers?: boolean;
}

/** Egy üzenet a postafiók-listában (teljes szöveg nélkül). */
export interface MessageListItem {
    messageId: string;
    subject: string;
    preview: string;
    senderName: string;
    /** A küldő azonosítója — a „Válasz” gombhoz. Null, ha a fiók már nincs meg. */
    senderId?: string | null;
    sentDate: string;
    isRead: boolean;
    eventId?: string | null;
}

/** Egy üzenet teljes tartalma. */
export interface MessageDetail {
    messageId: string;
    subject: string;
    body: string;
    senderName: string;
    /** A küldő azonosítója — a „Válasz” gombhoz. Null, ha a fiók már nincs meg. */
    senderId?: string | null;
    sentDate: string;
    isRead: boolean;
    eventId?: string | null;
    eventName?: string | null;
}

/** Egy KIKÜLDÖTT üzenet az admin listájában. */
export interface SentMessageListItem {
    messageId: string;
    subject: string;
    preview: string;
    senderName: string;
    /** A küldő azonosítója — a „Válasz” gombhoz. Null, ha a fiók már nincs meg. */
    senderId?: string | null;
    sentDate: string;
    eventId?: string | null;
    eventName?: string | null;
    recipientCount: number;
    readCount: number;
}

export interface SentMessageRecipient {
    userId: string;
    name: string;
    /** Csak adminnak jön; edző/játékos saját elküldött üzeneténél null. */
    email: string | null;
    isRead: boolean;
    readDate?: string | null;
}

export interface SentMessageDetail extends SentMessageListItem {
    body: string;
    recipients: SentMessageRecipient[];
}

/** Egy választható címzett a saját listámban — csak név és szerepkör. */
export interface MessageRecipientOption {
    userId: string;
    name: string;
    roleName: string;
    roleDisplayName: string;
}

/** Egyéni üzenet EGY címzettnek. Szándékosan nem lista: csoportos küldés nincs. */
export interface SendDirectMessageRequest {
    recipientUserId: string;
    subject: string;
    body: string;
}

export const messageService = {
    async sendMessage(data: SendMessageRequest): Promise<{ success: boolean; message: string; details: any }> {
        const response = await api.post<{ success: boolean; message: string; details: any }>('/messages/send', data);
        return response.data;
    },

    /** A bejelentkezett felhasználó postafiókja, legújabb elöl. */
    async getMyMessages(): Promise<MessageListItem[]> {
        const response = await api.get<MessageListItem[]>('/messages/my');
        return response.data;
    },

    /** Egy üzenet teljes tartalma. A lekérés egyben olvasottá is teszi. */
    async getById(messageId: string): Promise<MessageDetail> {
        const response = await api.get<MessageDetail>(`/messages/${messageId}`);
        return response.data;
    },

    async getUnreadCount(): Promise<number> {
        const response = await api.get<{ unreadCount: number }>('/messages/unread-count');
        return response.data?.unreadCount ?? 0;
    },

    async markAllRead(): Promise<void> {
        await api.post('/messages/mark-all-read');
    },

    /**
     * Eldobja az üzenetet a SAJÁT postafiókból. A többi címzettnél érintetlen marad —
     * az üzenetből egy van, a példány-sorokból annyi, ahány címzett.
     */
    async remove(messageId: string): Promise<void> {
        await api.delete(`/messages/${messageId}`);
    },

    /** Admin: a kiküldött üzenetek, lapozva. */
    /**
     * A bejelentkezett felhasználó SAJÁT engedélyezett címzettlistája. A szabály a szerveren
     * él (edző → díjas játékos, játékos → edző/admin, admin → bárki); a lista csak kényelem,
     * a küldést a szerver újra ellenőrzi.
     */
    async getRecipients(): Promise<MessageRecipientOption[]> {
        const response = await api.get<MessageRecipientOption[]>('/messages/recipients');
        return response.data;
    },

    /** Egyéni üzenet egy címzettnek (edző, játékos, admin egyaránt). */
    async sendDirect(data: SendDirectMessageRequest): Promise<{ messageId: string }> {
        const response = await api.post<{ messageId: string }>('/messages/send-direct', data);
        return response.data;
    },

    /**
     * Eltünteti a SAJÁT elküldött üzenetet az elküldött-listából. A címzetteknél megmarad —
     * ugyanaz az elv, mint a postafiók törlésénél: jelölés, nem törlés.
     */
    async removeSent(messageId: string): Promise<void> {
        await api.delete(`/messages/sent/${messageId}`);
    },

    async getSentMessages(skip = 0, take = 50): Promise<SentMessageListItem[]> {
        const response = await api.get<SentMessageListItem[]>('/messages/sent', { params: { skip, take } });
        return response.data;
    },

    /** Admin: egy kiküldött üzenet teljes szövege és címzettjei. */
    async getSentMessage(messageId: string): Promise<SentMessageDetail> {
        const response = await api.get<SentMessageDetail>(`/messages/sent/${messageId}`);
        return response.data;
    },
};

/**
 * Igaz, ha a hiba azért jött, mert a felhasználó KORÁBBAN maga törölte az üzenetet
 * (a szerver 410 Gone-t ad) — ez nem hálózati hiba, és az újrapróbálás sem segítene.
 */
export function isDeletedMessageError(error: any): boolean {
    return error?.response?.status === 410;
}
